import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"

type WebhookEvent = {
  id: number
  endpoint: string
  event_type: string
  http_method: string
  result_status: string
  response_status: number | null
  received_at: string
  customer_id: number | null
  lead_id: number | null
  fieldroutes_customer_id: string | null
  fieldroutes_customer_number: string | null
  fieldroutes_lead_id: string | null
  fieldroutes_subscription_id: string | null
  action_taken: string | null
  summary: string | null
  error_message: string | null
}

type Pagination = { page: number; per_page: number; total_count: number; total_pages: number; prev_page: number | null; next_page: number | null }

type FilterOption = { label: string; value: string }

type Props = {
  events: WebhookEvent[]
  filters: Record<string, string>
  filter_options: { endpoints: FilterOption[]; statuses: FilterOption[] }
  pagination: Pagination
}

export default function AdminFieldRoutesWebhooksIndex({ events, filters, filter_options, pagination }: Props) {
  const currentFilters = {
    status: filters.status ?? "",
    endpoint: filters.endpoint ?? "",
    customer_id: filters.customer_id ?? "",
    lead_id: filters.lead_id ?? "",
    subscription_id: filters.subscription_id ?? "",
  }

  const submitFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const values = Object.fromEntries(new FormData(event.currentTarget))
    router.get(
      "/admin/field-routes-webhooks",
      {
        status: String(values.status ?? ""),
        endpoint: String(values.endpoint ?? ""),
        customer_id: String(values.customer_id ?? ""),
        lead_id: String(values.lead_id ?? ""),
        subscription_id: String(values.subscription_id ?? ""),
        page: 1,
        per_page: pagination.per_page,
      },
      { preserveState: true, preserveScroll: true, replace: true },
    )
  }

  const goToPage = (page: number) => {
    router.get(
      "/admin/field-routes-webhooks",
      { ...currentFilters, page, per_page: pagination.per_page },
      { preserveState: true, preserveScroll: true, replace: true },
    )
  }

  return (
    <>
      <Head title="Webhook Logs | LeadFlow">
        <meta name="description" content="Review FieldRoutes webhook audit events." />
        <meta property="og:title" content="Webhook Logs | LeadFlow" />
        <meta property="og:description" content="Review FieldRoutes webhook audit events." />
      </Head>
      <AdminShell wide>
        <PageHeader title="Webhook Logs" description="Recent FieldRoutes webhook requests, results, and troubleshooting details." />

        <form className="mt-6 grid gap-3 rounded-lg border border-hairline bg-surface p-4 lg:grid-cols-5" onSubmit={submitFilters}>
          <label>
            Status
            <Select name="status" defaultValue={currentFilters.status}>
              <option value="">All statuses</option>
              {filter_options.statuses.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Select>
          </label>
          <label>
            Endpoint
            <Select name="endpoint" defaultValue={currentFilters.endpoint}>
              <option value="">All endpoints</option>
              {filter_options.endpoints.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Select>
          </label>
          <label>
            Customer ID
            <Input name="customer_id" defaultValue={currentFilters.customer_id} placeholder="C-123" />
          </label>
          <label>
            Lead ID
            <Input name="lead_id" defaultValue={currentFilters.lead_id} placeholder="L-123" />
          </label>
          <label>
            Subscription ID
            <Input name="subscription_id" defaultValue={currentFilters.subscription_id} placeholder="S-123" />
          </label>
          <div className="flex items-end gap-2 lg:col-span-5">
            <Button><Search className="h-4 w-4" />Apply</Button>
            <Button type="button" variant="ghost" onClick={() => router.get("/admin/field-routes-webhooks", { per_page: pagination.per_page }, { preserveState: true, replace: true })}>Clear</Button>
          </div>
        </form>

        <div className="mt-6 overflow-hidden rounded-lg border border-hairline bg-page">
          <div className="border-b border-hairline px-4 py-3 text-sm text-ink-muted">
            Showing {pagination.total_count === 0 ? 0 : (pagination.page - 1) * pagination.per_page + 1}–{Math.min(pagination.page * pagination.per_page, pagination.total_count)} of {pagination.total_count}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-hairline text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wide text-ink-muted">
                <tr>
                  <Th>Received</Th>
                  <Th>Endpoint</Th>
                  <Th>Status</Th>
                  <Th>HTTP</Th>
                  <Th>Customer ID</Th>
                  <Th>Lead ID</Th>
                  <Th>Subscription ID</Th>
                  <Th>Action</Th>
                  <Th>Error</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {events.map((event) => (
                  <tr key={event.id} className="align-top hover:bg-surface/60">
                    <Td>
                      <Link href={`/admin/field-routes-webhooks/${event.id}`} className="no-underline hover:underline">
                        {formatDateTime(event.received_at)}
                      </Link>
                    </Td>
                    <Td>{event.endpoint}</Td>
                    <Td><StatusBadge status={event.result_status} /></Td>
                    <Td>{event.response_status ?? "—"}</Td>
                    <Td>{event.fieldroutes_customer_id || "—"}</Td>
                    <Td>{event.fieldroutes_lead_id || "—"}</Td>
                    <Td>{event.fieldroutes_subscription_id || "—"}</Td>
                    <Td className="max-w-[18rem]">{event.action_taken || event.summary || "—"}</Td>
                    <Td className="max-w-[18rem] text-danger-display">{event.error_message || "—"}</Td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-ink-muted">No webhook events match these filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ink-muted">
            Page {pagination.page} of {pagination.total_pages}. {pagination.total_count} webhook events total.
          </p>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" disabled={!pagination.prev_page} onClick={() => pagination.prev_page && goToPage(pagination.prev_page)}>Previous</Button>
            <Button type="button" variant="ghost" disabled={!pagination.next_page} onClick={() => pagination.next_page && goToPage(pagination.next_page)}>Next</Button>
          </div>
        </div>
      </AdminShell>
    </>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-3 font-medium">{children}</th>
}

function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>
}

function StatusBadge({ status }: { status: string }) {
  const tone = status === "error" || status === "rejected" ? "signal" : "accent"
  return <Badge tone={tone}>{status}</Badge>
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString()
}
